import type { ConversationListItemResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/conversation";
import type { ShopperAiIntegrationType } from "@redotech/effect-api-shared/models/shopper-ai/integration/integration";
import { formatTimeAgo } from "@redotech/util/date-format";
import { useMemo } from "react";

export interface UseConversationInfoCardProps {
  conversation: ConversationListItemResponse;
}

export interface UseConversationInfoCardResult {
  channels: readonly ShopperAiIntegrationType[];
  isUnread: boolean;
  timeSinceMessage: string;
  lastMessageDate: Date | null;
}

/**
 * Hook to derive display data for a conversation info card.
 * - channels: integration types from conversation.channels
 * - isUnread: true if lastCustomerMessageAt > readAt
 * - timeSinceMessage: relative time string (e.g., "5m", "2h", "3d")
 * - lastMessageDate: the raw Date for tooltip display
 */
export function useConversationInfoCard({
  conversation,
}: UseConversationInfoCardProps): UseConversationInfoCardResult {
  const { isUnread, timeSinceMessage, lastMessageDate } = useMemo(() => {
    const lastCustomerMessage = conversation.lastCustomerMessageAt;
    const readAt = conversation.readAt;

    // Determine if unread: has customer message after last read time
    const isUnread =
      lastCustomerMessage !== null &&
      (readAt === null || lastCustomerMessage > readAt);

    // Use the most recent message time (customer or merchant)
    const lastMessageDate =
      getMoreRecent(
        conversation.lastCustomerMessageAt,
        conversation.lastMerchantMessageAt,
      ) ?? null;

    const timeSinceMessage = lastMessageDate
      ? formatTimeAgo(lastMessageDate.toISOString())
      : "";

    return { isUnread, timeSinceMessage, lastMessageDate };
  }, [
    conversation.lastCustomerMessageAt,
    conversation.lastMerchantMessageAt,
    conversation.readAt,
  ]);

  return {
    channels: conversation.channels,
    isUnread,
    timeSinceMessage,
    lastMessageDate,
  };
}

function getMoreRecent(a: Date | null, b: Date | null): Date | null {
  if (a === null) {
    return b;
  }
  if (b === null) {
    return a;
  }
  return a > b ? a : b;
}
